import { useCallback, useState } from 'react';

import type { InvestingBoardMoveData, InvestingBoardPledgedDto } from '@/api/investing-board/types';
import type { PipelineDragMove } from '@/pages/pipelines/common/types';

const INVESTED_STAGE = 'Invested';

type MoveEntityCard = (_payload: {
  entryId: string;
  targetStage: string;
  investmentMoveData?: InvestingBoardMoveData;
}) => Promise<unknown>;

export type PendingFundsReceived = {
  move: PipelineDragMove;
  pledged: InvestingBoardPledgedDto;
};

type PendingState = {
  request: PendingFundsReceived;
  resolve: () => void;
  reject: (_error: unknown) => void;
};

type UseEntityBoardMoveOptions = {
  moveEntityCard: MoveEntityCard;
  findPledged: (_cardId: string) => InvestingBoardPledgedDto | null;
  /** Called instead of opening the dialog when the dropped card has no pledged figures to record against. */
  onRefuse?: (_cardId: string) => void;
};

/**
 * Wraps the entity board's move call so a drop on `Invested` collects the received
 * amount first, instead of moving straight through like every other stage.
 *
 * The promise `onCardMove` returns for that drop stays pending — neither
 * resolving nor rejecting — for as long as the confirm dialog is open. That is
 * what makes drag-and-drop's own revert-on-catch handling (`usePipelineCardDrag`)
 * put a cancelled or failed card back in its source column instead of leaving it
 * stuck in `Invested`.
 */
export const useEntityBoardMove = ({
  moveEntityCard,
  findPledged,
  onRefuse,
}: UseEntityBoardMoveOptions) => {
  const [pending, setPending] = useState<PendingState | null>(null);

  const onCardMove = useCallback(
    (move: PipelineDragMove): Promise<void> => {
      if (move.targetStage !== INVESTED_STAGE) {
        return moveEntityCard({ entryId: move.cardId, targetStage: move.targetStage }).then(
          () => undefined,
        );
      }

      const pledged = findPledged(move.cardId);
      if (!pledged) {
        onRefuse?.(move.cardId);
        return Promise.reject(new Error('No pledged amount to confirm against'));
      }

      return new Promise<void>((resolve, reject) => {
        setPending({ request: { move, pledged }, resolve, reject });
      });
    },
    [findPledged, moveEntityCard, onRefuse],
  );

  const confirm = useCallback(
    async (data: InvestingBoardMoveData) => {
      if (!pending) return;

      try {
        await moveEntityCard({
          entryId: pending.request.move.cardId,
          targetStage: INVESTED_STAGE,
          investmentMoveData: data,
        });
        pending.resolve();
      } catch (error) {
        pending.reject(error);
      } finally {
        setPending(null);
      }
    },
    [moveEntityCard, pending],
  );

  const cancel = useCallback(() => {
    pending?.reject(new Error('Cancelled'));
    setPending(null);
  }, [pending]);

  return { onCardMove, pendingFundsReceived: pending?.request ?? null, confirm, cancel };
};
